/**
 * WorksBudgetSection - Budget travaux du projet.
 *
 * Affiche et permet d'editer :
 *  - le montant debloque par le credit pour les travaux
 *  - l'enveloppe travaux prevue (pour calculer la marge)
 *  - la liste des intervenants avec leur cout (HT + TTC) et statut payee/non
 *
 * Calcule en live :
 *  - total engage (HT/TTC)
 *  - total paye
 *  - reste a payer (engage - paye)
 *  - reste a debloquer (engage - debloque)
 *  - marge restante (enveloppe - engage)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { AutoSaveIndicator } from '../hooks/useAutoSave';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null) return '—';
  return Math.round(n).toLocaleString('fr-FR') + ' EUR';
};

const WorksBudgetSection = ({ projectId, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const [autoSaveError, setAutoSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  // Iter 88 : annuaire artisans pour autocompletion email
  const [contractors, setContractors] = useState([]);
  // editing reste true en permanence : tous les champs sont editables et l'enregistrement
  // est automatique (debounce 1.2s) via useEffect plus bas.
  const editing = true;
  const [draft, setDraft] = useState({
    allocated_total: 0, enveloppe_initial: 0, interventions: [], note: '',
    foncier_net: 0, frais_agence: 0, frais_notaire: 0,
    garantie_hypothecaire: 0, frais_dossier_bancaire: 0, frais_courtage: 0,
    honoraires_realease: 0, honoraires_realease_locked: false,
    honoraires_expected_billing_date: null, apport: 0,
    extra_fees: [],
  });
  // Refs pour le moteur d'auto-save
  const lastSavedJsonRef = useRef(null);
  const saveTimerRef = useRef(null);
  const inFlightRef = useRef(false);
  const draftRef = useRef(draft);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/works-budget`);
      setData(r.data);
      const wb = r.data.works_budget || {};
      const sum = r.data.summary || {};
      const initialDraft = {
        allocated_total: wb.allocated_total || 0,
        enveloppe_initial: wb.enveloppe_initial || 0,
        interventions: wb.interventions || [],
        note: wb.note || '',
        foncier_net: wb.foncier_net != null ? wb.foncier_net : (sum.foncier_net || 0),
        frais_agence: wb.frais_agence || 0,
        frais_notaire: wb.frais_notaire || 0,
        garantie_hypothecaire: wb.garantie_hypothecaire || 0,
        frais_dossier_bancaire: wb.frais_dossier_bancaire || 0,
        frais_courtage: wb.frais_courtage || 0,
        honoraires_realease: wb.honoraires_realease != null ? wb.honoraires_realease : sum.honoraires_realease,
        honoraires_realease_locked: !!wb.honoraires_realease_locked,
        honoraires_expected_billing_date: wb.honoraires_expected_billing_date || sum.honoraires_expected_billing_date || null,
        apport: wb.apport || 0,
        extra_fees: wb.extra_fees || [],
      };
      setDraft(initialDraft);
      // Memorise l'etat charge pour eviter un auto-save inutile au premier render
      lastSavedJsonRef.current = JSON.stringify(initialDraft);
      setAutoSaveStatus('saved');
      setSavedAt(new Date());
    } catch (e) {
      // silencieux
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // Iter 88 : charge l'annuaire artisans pour autocompletion email
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/contractors`);
        if (!cancelled) setContractors(r.data || []);
      } catch {
        // silencieux : autocomplete optionnel
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const buildPayload = useCallback((d) => ({
    allocated_total: parseFloat(d.allocated_total) || 0,
    enveloppe_initial: parseFloat(d.enveloppe_initial) || 0,
    interventions: (d.interventions || []).map(it => ({
      ...it,
      cost_ht: it.cost_ht === '' || it.cost_ht == null ? null : parseFloat(it.cost_ht),
      cost_ttc: it.cost_ttc === '' || it.cost_ttc == null ? null : parseFloat(it.cost_ttc),
      status: it.status || null,
    })),
    note: d.note || '',
    foncier_net: parseFloat(d.foncier_net) || 0,
    frais_agence: parseFloat(d.frais_agence) || 0,
    frais_notaire: parseFloat(d.frais_notaire) || 0,
    garantie_hypothecaire: parseFloat(d.garantie_hypothecaire) || 0,
    frais_dossier_bancaire: parseFloat(d.frais_dossier_bancaire) || 0,
    frais_courtage: parseFloat(d.frais_courtage) || 0,
    honoraires_realease: parseFloat(d.honoraires_realease) || 0,
    honoraires_realease_locked: !!d.honoraires_realease_locked,
    honoraires_expected_billing_date: d.honoraires_expected_billing_date || null,
    apport: parseFloat(d.apport) || 0,
    extra_fees: (d.extra_fees || []).map(f => ({ label: f.label || '', amount: parseFloat(f.amount) || 0 })),
  }), []);

  // Auto-save debounce 1.2s
  useEffect(() => {
    if (loading || !projectId) return;
    const currentJson = JSON.stringify(draft);
    if (lastSavedJsonRef.current === null) {
      lastSavedJsonRef.current = currentJson;
      return;
    }
    if (currentJson === lastSavedJsonRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setAutoSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const snapshot = draftRef.current;
        const r = await axios.put(`${API}/projects/${projectId}/works-budget`, buildPayload(snapshot));
        setData(r.data);
        // Iter 93 : merge des ids assignes par le backend dans le draft (positionnel) pour
        // que les prochains PUT renvoient le meme id et evitent de creer un doublon dans
        // le planning a chaque caractere tape.
        const backendIvs = r.data.works_budget?.interventions || [];
        setDraft((prev) => {
          let changed = false;
          const merged = (prev.interventions || []).map((it, i) => {
            if (it.id) return it;
            const bk = backendIvs[i];
            if (bk?.id) { changed = true; return { ...it, id: bk.id }; }
            return it;
          });
          if (!changed) return prev;
          const next = { ...prev, interventions: merged };
          // Met a jour la baseline pour eviter de declencher un auto-save inutile
          lastSavedJsonRef.current = JSON.stringify(next);
          return next;
        });
        lastSavedJsonRef.current = JSON.stringify(snapshot);
        setAutoSaveStatus('saved');
        setSavedAt(new Date());
        setAutoSaveError(null);
        onSaved && onSaved();
      } catch (e) {
        setAutoSaveStatus('error');
        setAutoSaveError(e.response?.data?.detail || e.message);
      } finally {
        inFlightRef.current = false;
      }
    }, 1200);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [draft, loading, projectId, buildPayload]);

  // Note: l'enregistrement manuel n'est plus expose en UI (auto-save gere tout).

  const updateIntervention = (idx, field, value) => {
    setDraft((prev) => {
      const next = [...prev.interventions];
      next[idx] = { ...next[idx], [field]: value };
      // Auto-calcul TTC si on remplit HT et pas TTC (TVA 20%)
      if (field === 'cost_ht' && (!next[idx].cost_ttc || next[idx].cost_ttc === '')) {
        const ht = parseFloat(value);
        if (!isNaN(ht)) next[idx].cost_ttc = (ht * 1.20).toFixed(2);
      }
      // Synchronise paid <-> status
      if (field === 'status') {
        next[idx].paid = (value === 'paye');
      }
      if (field === 'paid') {
        next[idx].status = value ? 'paye' : (next[idx].status === 'paye' ? null : next[idx].status);
      }
      return { ...prev, interventions: next };
    });
  };

  const addIntervention = () => {
    // Iter 93 : assigne un id client stable des la creation pour eviter que le backend
    // genere un nouvel id a chaque auto-save (sinon cree des doublons d'items planning).
    const clientId = `int_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36).slice(-4)}`;
    setDraft({
      ...draft,
      interventions: [...draft.interventions, { id: clientId, intervenant: '', description: '', cost_ht: '', cost_ttc: '', paid: false, status: null }],
    });
  };

  const removeIntervention = (idx) => {
    if (!window.confirm('Supprimer cette intervention ?')) return;
    setDraft({ ...draft, interventions: draft.interventions.filter((_, i) => i !== idx) });
  };

  // ===== Frais supplementaires (extra_fees) =====
  const addExtraFee = () => {
    setDraft({ ...draft, extra_fees: [...(draft.extra_fees || []), { label: '', amount: '' }] });
  };
  const removeExtraFee = (idx) => {
    setDraft({ ...draft, extra_fees: draft.extra_fees.filter((_, i) => i !== idx) });
  };
  const updateExtraFee = (idx, field, value) => {
    const next = [...(draft.extra_fees || [])];
    next[idx] = { ...next[idx], [field]: value };
    setDraft({ ...draft, extra_fees: next });
  };

  if (loading) {
    return <div className="realease-card p-6"><p className="text-sm text-gray-400 italic">Chargement du budget travaux...</p></div>;
  }

  const summary = data?.summary || {};
  const interventions = (editing ? draft.interventions : (data?.works_budget?.interventions || []));

  return (
    <div className="realease-card p-0 overflow-hidden" data-testid="works-budget-section">
      {/* Header avec autosave indicator (plus de bouton Modifier/Enregistrer : auto-save) */}
      <div className="bg-gradient-to-r from-[#13736D]/10 to-[#008A8D]/5 border-b border-[#13736D]/20 px-5 py-4 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Budget travaux
          <span className="text-[10px] uppercase tracking-wide text-gray-500 ml-1">Auto-save</span>
        </h3>
        <div className="flex items-center gap-3" data-testid="works-budget-autosave">
          <AutoSaveIndicator status={autoSaveStatus} savedAt={savedAt} error={autoSaveError} />
        </div>
      </div>

      {/* Iter 88 - Refonte : Enveloppe travaux simplifiee.
          Plus de "Total projet TTC" / foncier / frais / apport (deja sur l'offre de pret).
          On garde uniquement : enveloppe empruntee + honoraires + interventions TTC. */}
      <div className="p-5 border-b border-gray-100" data-testid="works-budget-envelope">
        <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
          <span className="w-1.5 h-5 bg-[#13736D] rounded"></span>
          Enveloppe travaux
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-[#13736D]/5 border border-[#13736D]/30">
            <label className="text-[11px] font-semibold text-[#13736D] uppercase tracking-wide">Montant emprunte (travaux)</label>
            {editing ? (
              <input type="number" min="0" step="100" value={draft.enveloppe_initial}
                     onChange={e => setDraft({ ...draft, enveloppe_initial: e.target.value })}
                     placeholder="Ex: 50000"
                     className="realease-input mt-1 text-base font-bold"
                     data-testid="works-enveloppe-input" />
            ) : (
              <p className="text-2xl font-bold text-[#13736D] mt-1" data-testid="works-enveloppe-display">{fmt(summary.enveloppe_initial)}</p>
            )}
            <p className="text-[10px] text-gray-500 mt-1">Total emprunte a la banque pour les travaux (inclut honoraires).</p>
          </div>
          <div className="p-3 rounded-lg bg-[#A57F4F]/5 border border-[#A57F4F]/30">
            <div className="flex items-center justify-between flex-wrap gap-1">
              <label className="text-[11px] font-semibold text-[#A57F4F] uppercase tracking-wide">Honoraires RealEase</label>
              {editing && (
                <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={!draft.honoraires_realease_locked}
                         onChange={e => setDraft({ ...draft, honoraires_realease_locked: !e.target.checked })}
                         className="w-3 h-3 accent-[#A57F4F]"
                         data-testid="works-honoraires-auto-toggle" />
                  Auto (depuis Analyse)
                </label>
              )}
            </div>
            {editing ? (
              <input type="number" min="0" step="100"
                     value={draft.honoraires_realease_locked ? (draft.honoraires_realease ?? '') : (summary.honoraires_realease ?? 0)}
                     onChange={e => setDraft({ ...draft, honoraires_realease: e.target.value, honoraires_realease_locked: true })}
                     disabled={!draft.honoraires_realease_locked}
                     className={`realease-input mt-1 text-base font-bold ${!draft.honoraires_realease_locked ? 'bg-gray-50 text-[#A57F4F]' : ''}`}
                     data-testid="works-honoraires-input" />
            ) : (
              <p className="text-2xl font-bold text-[#A57F4F] mt-1" data-testid="works-honoraires-display">{fmt(summary.honoraires_realease)}</p>
            )}
            <p className="text-[10px] text-gray-500 mt-1">
              {summary.honoraires_source === 'analysis' && 'Repris automatiquement depuis l\'Analyse du projet.'}
              {summary.honoraires_source === 'auto' && 'Calcul automatique au bareme contrat.'}
              {summary.honoraires_source === 'locked' && 'Saisie manuelle (verrouillee).'}
            </p>
            {/* Date prevue de facturation */}
            <div className="mt-2 pt-2 border-t border-[#A57F4F]/20 flex items-center gap-2">
              <label className="text-[10px] font-semibold text-[#A57F4F] uppercase whitespace-nowrap">Facturation prevue</label>
              <input type="date"
                     value={(draft.honoraires_expected_billing_date || '').slice(0, 10)}
                     onChange={e => setDraft({ ...draft, honoraires_expected_billing_date: e.target.value || null })}
                     className="border border-gray-200 rounded px-2 py-0.5 text-xs bg-white"
                     data-testid="works-honoraires-billing-date-input" />
            </div>
          </div>
        </div>

        {/* Jauge d'utilisation de l'enveloppe (visuelle pour le client) */}
        {(() => {
          const env = Number(summary.enveloppe_initial) || 0;
          const hono = Number(summary.honoraires_realease) || 0;
          const interventionsTotalTtc = Number(summary.total_engaged_ttc) || 0;
          const used = hono + interventionsTotalTtc;
          const remaining = env - used;
          const usedPct = env > 0 ? Math.min(100, (used / env) * 100) : 0;
          const honoPct = env > 0 ? Math.min(100, (hono / env) * 100) : 0;
          const interventionsPct = env > 0 ? Math.min(100 - honoPct, (interventionsTotalTtc / env) * 100) : 0;
          const overBudget = used > env && env > 0;
          return (
            <div className="mt-5" data-testid="works-budget-gauge">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="font-semibold text-gray-700">Utilisation de l&apos;enveloppe</span>
                <span className="text-gray-500">{fmt(used)} / {fmt(env)} TTC</span>
              </div>
              <div className="relative w-full h-6 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                <div className="absolute inset-y-0 left-0 bg-[#A57F4F] transition-all" style={{ width: `${honoPct}%` }} title={`Honoraires : ${fmt(hono)}`}></div>
                <div className="absolute inset-y-0 bg-[#13736D] transition-all" style={{ left: `${honoPct}%`, width: `${interventionsPct}%` }} title={`Interventions : ${fmt(interventionsTotalTtc)}`}></div>
                {overBudget && (
                  <div className="absolute inset-y-0 right-0 bg-red-500 flex items-center justify-end pr-2 text-white text-[10px] font-bold">
                    Depassement
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between mt-2 text-xs flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#A57F4F] rounded-sm"></span> Honoraires</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#13736D] rounded-sm"></span> Interventions</span>
                </div>
                <div className={`px-3 py-1 rounded-full font-bold text-sm ${remaining >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`} data-testid="works-budget-remaining">
                  {remaining >= 0 ? `${fmt(remaining)} restants` : `${fmt(Math.abs(remaining))} de depassement`}
                </div>
              </div>

              {/* Barre de progression du budget travaux (engage / paye vs enveloppe) */}
              {(() => {
                const env = Number(draft.enveloppe_initial) || 0;
                const moTtc = Number(data?.works_budget?.maitrise_oeuvre?.enabled ? data.works_budget.maitrise_oeuvre.total_ttc : 0) || 0;
                const engaged = (interventions || []).reduce((s, it) => s + (Number(it.cost_ttc) || 0), 0) + moTtc;
                const paid = (interventions || []).reduce((s, it) => s + ((it.status === 'paye' || it.paid) ? (Number(it.cost_ttc) || 0) : 0), 0);
                if (env <= 0 && engaged <= 0) return null;
                const base = Math.max(env, engaged);
                return (
                  <div className="mt-4 space-y-2" data-testid="works-budget-progress">
                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>Progression du budget {env > 0 ? `(enveloppe ${fmt(env)})` : ''}</span>
                      <span><b className="text-gray-700">{fmt(engaged)}</b> engagés · <b className="text-emerald-700">{fmt(paid)}</b> payés</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden relative">
                      <div className={`h-full rounded-full ${env > 0 && engaged > env ? 'bg-red-400' : 'bg-[#A57F4F]'}`} style={{ width: `${Math.min(100, (engaged / (base || 1)) * 100)}%` }} />
                      <div className="h-full bg-emerald-500 rounded-full absolute top-0 left-0" style={{ width: `${Math.min(100, (paid / (base || 1)) * 100)}%` }} />
                    </div>
                    {env > 0 && engaged > env && (
                      <p className="text-[10px] text-red-600 font-semibold">Dépassement de l'enveloppe : {fmt(engaged - env)}</p>
                    )}
                  </div>
                );
              })()}

              {/* Iter 89 : Camembert (donut) repartition par intervenant (+ MO si activee) */}
              {((interventions || []).length > 0 || data?.works_budget?.maitrise_oeuvre?.enabled) && (() => {
                const COLORS = ['#13736D', '#A57F4F', '#008A8D', '#E89B5F', '#5FAFA8', '#C39F6F', '#3D8077', '#D4B589', '#0F5C57', '#E5C39A'];
                const moTtc = Number(data?.works_budget?.maitrise_oeuvre?.enabled ? data.works_budget.maitrise_oeuvre.total_ttc : 0) || 0;
                const totalIntv = interventions.reduce((s, it) => s + (Number(it.cost_ttc) || 0), 0) + moTtc;
                if (totalIntv === 0) return null;
                const baseParts = interventions
                  .filter(it => (Number(it.cost_ttc) || 0) > 0)
                  .map((it) => ({ label: it.intervenant || 'Sans nom', amount: Number(it.cost_ttc) || 0 }));
                if (moTtc > 0) baseParts.push({ label: "Maîtrise d'œuvre", amount: moTtc });
                const parts = baseParts.map((p, i) => ({
                  ...p,
                  pct: (p.amount / totalIntv) * 100,
                  color: p.label === "Maîtrise d'œuvre" ? '#E89B5F' : COLORS[i % COLORS.length],
                }));
                // Construit les segments SVG en stroke-dasharray sur un cercle r=15.9155 (perimeter=100)
                let offset = 25; // start at top
                const segments = parts.map(p => {
                  const seg = { ...p, dash: `${p.pct} ${100 - p.pct}`, dashOffset: offset };
                  offset -= p.pct;
                  return seg;
                });
                return (
                  <div className="mt-6 pt-5 border-t border-gray-100" data-testid="works-budget-pie">
                    <h5 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Repartition par intervenant</h5>
                    <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4 items-center">
                      <svg viewBox="0 0 36 36" className="w-44 h-44 mx-auto" data-testid="works-budget-pie-svg">
                        <circle cx="18" cy="18" r="15.9155" fill="white" stroke="#f3f4f6" strokeWidth="3.5" />
                        {segments.map((s, i) => (
                          <circle key={i} cx="18" cy="18" r="15.9155" fill="transparent"
                                  stroke={s.color} strokeWidth="3.5"
                                  strokeDasharray={s.dash} strokeDashoffset={s.dashOffset}
                                  transform="rotate(-90 18 18)" />
                        ))}
                        <text x="18" y="17" textAnchor="middle" className="text-[3.5px] font-bold fill-gray-700">{fmt(totalIntv)}</text>
                        <text x="18" y="22" textAnchor="middle" className="text-[2.5px] fill-gray-500">total interventions</text>
                      </svg>
                      <ul className="space-y-1 text-xs">
                        {parts.map((p, i) => (
                          <li key={i} className="flex items-center gap-2" data-testid={`works-budget-pie-item-${i}`}>
                            <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: p.color }}></span>
                            <span className="flex-1 truncate text-gray-700 font-medium">{p.label}</span>
                            <span className="text-gray-500 tabular-nums">{p.pct.toFixed(1)} %</span>
                            <span className="text-gray-800 font-semibold tabular-nums w-20 text-right">{fmt(p.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </div>

      {/* Liste des intervenants */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-semibold text-gray-800 text-sm">Intervenants ({interventions.length})</p>
          {editing && (
            <button onClick={addIntervention} className="text-xs font-semibold text-[#13736D] hover:text-[#0F5C57]" data-testid="works-add-intervention">
              + Ajouter un intervenant
            </button>
          )}
        </div>
        {interventions.length === 0 ? (
          <p className="text-xs text-gray-400 italic text-center py-4">
            Aucune intervention. {!editing && <button onClick={addIntervention} className="text-[#13736D] hover:underline">Ajoutez la premiere</button>}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="works-interventions-table">
              <thead className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="text-left py-2 pr-2">Intervenant</th>
                  <th className="text-left py-2 pr-2">Description</th>
                  <th className="text-left py-2 pr-2">Intervention (debut → fin)</th>
                  <th className="text-right py-2 pr-2">Cout TTC</th>
                  <th className="text-center py-2 pr-2">Statut</th>
                  {editing && <th></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {interventions.map((it, idx) => (
                  <React.Fragment key={it.id || idx}>
                  <tr data-testid={`works-intervention-row-${idx}`}>
                    {editing ? (
                      <>
                        <td className="py-2 pr-2">
                          <input type="text"
                                 list={`contractors-companies-${idx}`}
                                 value={it.intervenant || ''}
                                 onChange={e => {
                                   const name = e.target.value;
                                   updateIntervention(idx, 'intervenant', name);
                                   // Si match dans l'annuaire et email vide -> pre-remplit
                                   const match = contractors.find(c => (c.company_name || '').toLowerCase() === name.toLowerCase());
                                   if (match && match.email && !it.artisan_email) {
                                     updateIntervention(idx, 'artisan_email', match.email);
                                   }
                                 }}
                                 placeholder="Nom de l'entreprise" className="w-full border border-gray-200 rounded px-2 py-1 text-sm" />
                          <datalist id={`contractors-companies-${idx}`}>
                            {contractors.map(c => (
                              <option key={c.contractor_id} value={c.company_name}>
                                {c.trade ? `${c.trade}` : ''}{c.city ? ` · ${c.city}` : ''}
                              </option>
                            ))}
                          </datalist>
                        </td>
                        <td className="py-2 pr-2">
                          <input type="text" value={it.description || ''} onChange={e => updateIntervention(idx, 'description', e.target.value)}
                                 placeholder="Ex: Maconnerie" className="w-full border border-gray-200 rounded px-2 py-1 text-sm" />
                        </td>
                        <td className="py-2 pr-2 w-64">
                          <div className="flex items-center gap-1">
                            <input type="date" value={(it.start_date || '').slice(0, 10)}
                                   onChange={e => updateIntervention(idx, 'start_date', e.target.value || null)}
                                   className="border border-gray-200 rounded px-1.5 py-1 text-xs"
                                   title="Date de debut d'intervention"
                                   data-testid={`works-start-date-${idx}`} />
                            <span className="text-gray-300 text-xs">→</span>
                            <input type="date" value={(it.end_date || '').slice(0, 10)}
                                   min={(it.start_date || '').slice(0, 10) || undefined}
                                   onChange={e => updateIntervention(idx, 'end_date', e.target.value || null)}
                                   className="border border-gray-200 rounded px-1.5 py-1 text-xs"
                                   title="Date de fin (optionnelle)"
                                   data-testid={`works-end-date-${idx}`} />
                          </div>
                        </td>
                        <td className="py-2 pr-2 w-28">
                          <input type="number" min="0" step="100" value={it.cost_ttc ?? ''}
                                 onChange={e => {
                                   // Iter 91 : single update (cost_ttc + cost_ht auto-derive) pour eviter
                                   // une race condition entre deux setDraft consecutifs qui ecrasaient cost_ttc.
                                   const raw = e.target.value;
                                   const ttc = parseFloat(raw);
                                   const ht = !isNaN(ttc) ? (ttc / 1.20).toFixed(2) : '';
                                   setDraft((prev) => {
                                     const next = [...prev.interventions];
                                     next[idx] = { ...next[idx], cost_ttc: raw, cost_ht: ht };
                                     return { ...prev, interventions: next };
                                   });
                                 }}
                                 placeholder="0" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right"
                                 data-testid={`works-cost-ttc-${idx}`} />
                        </td>
                        <td className="py-2 pr-2 text-center">
                          <select
                            value={it.status || (it.paid ? 'paye' : '')}
                            onChange={e => updateIntervention(idx, 'status', e.target.value || null)}
                            className="border border-gray-200 rounded px-1 py-1 text-xs"
                            data-testid={`works-status-select-${idx}`}
                          >
                            <option value="">— A definir —</option>
                            <option value="devis_envoye">Devis envoye</option>
                            <option value="devis_signe">Devis signe</option>
                            <option value="paye">Paye</option>
                          </select>
                        </td>
                        <td className="text-right">
                          <button onClick={() => removeIntervention(idx)} className="text-red-500 hover:text-red-700 text-xs px-1">✕</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 pr-2 font-medium text-gray-800">{it.intervenant || '—'}</td>
                        <td className="py-2 pr-2 text-gray-600 text-xs">{it.description || '—'}</td>
                        <td className="py-2 pr-2 text-xs text-gray-600 whitespace-nowrap">
                          {it.start_date
                            ? `${new Date(it.start_date).toLocaleDateString('fr-FR')}${it.end_date ? ` → ${new Date(it.end_date).toLocaleDateString('fr-FR')}` : ''}`
                            : '—'}
                        </td>
                        <td className="py-2 pr-2 text-right font-semibold">{fmt(it.cost_ttc)}</td>
                        <td className="py-2 pr-2 text-center">
                          {(() => {
                            const st = it.status || (it.paid ? 'paye' : '');
                            if (st === 'paye') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Paye</span>;
                            if (st === 'devis_signe') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Devis signe</span>;
                            if (st === 'devis_envoye') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Devis envoye</span>;
                            return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-gray-100 text-gray-700 rounded">A definir</span>;
                          })()}
                        </td>
                      </>
                    )}
                  </tr>
                  {/* Iter 87+88 : email artisan pour acces portail (autocomplete annuaire) */}
                  {editing && (
                    <tr className="border-b border-gray-100 bg-[#A57F4F]/5">
                      <td colSpan="6" className="py-1 px-2">
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <span className="text-[10px] uppercase text-gray-500 font-semibold whitespace-nowrap">Email artisan :</span>
                          <input type="email"
                                 list={`contractors-emails-${idx}`}
                                 value={it.artisan_email || ''}
                                 onChange={e => {
                                   const email = e.target.value;
                                   updateIntervention(idx, 'artisan_email', email);
                                   // Si match exact dans l'annuaire et intervenant vide -> pre-remplit
                                   const match = contractors.find(c => (c.email || '').toLowerCase() === email.toLowerCase());
                                   if (match && !it.intervenant) {
                                     updateIntervention(idx, 'intervenant', match.company_name || '');
                                   }
                                 }}
                                 placeholder="Tapez un email ou choisissez dans l'annuaire"
                                 className="flex-1 min-w-[200px] border border-gray-200 rounded px-2 py-1 text-xs bg-white"
                                 data-testid={`works-artisan-email-${idx}`} />
                          <datalist id={`contractors-emails-${idx}`}>
                            {contractors.filter(c => c.email).map(c => (
                              <option key={c.contractor_id} value={c.email}>
                                {c.company_name || ''}{c.trade ? ` — ${c.trade}` : ''}
                              </option>
                            ))}
                          </datalist>
                          {it.artisan_email && (
                            <span className="text-[10px] text-[#A57F4F] font-semibold" title="L'artisan verra cette intervention dans son portail">
                              ✓ Portail
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorksBudgetSection;
